import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import Product from "./Product";
import ModalFileUpload from "../../components/ModalFileUpload/ModalFileUpload";
import CommonDeleteModal from "../../components/commonDeleteModal/CommonDeleteModal";
import CommonSuccessModal from "../../components/commonSuccessModal/CommonSuccessModal";

import {
  API_STATE,
  ASSET_LOCATION_MAPPING,
  ASSET_TYPE_MAPPING,
  LABEL_DELETE_ASSET,
  FORM_TYPE,
  LANGUAGE_OPTIONS,
  STATUS_CODE_RESPONSE_OK,
  MESSAGE_ERROR_INPUT_REQUIRED,
} from "../../utils/constant";
import { isEmpty, isEmptyObj } from "../../utils/commonUtil";

import {
  fetchProduct,
  resetProductValue,
  setError,
  setValueBasicInfo,
  updateProduct,
} from "../../redux/productSlice";
import { deleteAsset, uploadAsset } from "../../redux/assetSlice";
import { createProducts } from "../../redux/productsSlice";

function ProductDetailEdit({
  productId,
  formType,
  initLanguage,
  handleComplete,
  handleBack,
}) {
  const dispatch = useDispatch();

  const TAB_PRODUCT_KEY = {
    basicInfo: "basicInfo",
  };

  const TAB_LIST_MAPPING = [
    {
      id: TAB_PRODUCT_KEY.basicInfo,
      label: "Basic Info",
      order: 1,
    },
  ];

  const PRODUCT_MODEL_TEMPLATE = [
    {
      id: "sku",
      label: "SKU",
      placeholder: {
        EN: "Enter SKU",
        CN: "输入SKU",
      },
      type: "text",
      required: true,
    },
    {
      id: "name",
      label: "Name",
      placeholder: {
        EN: "Enter Name",
        CN: "输入名称",
      },
      type: "text",
      required: true,
      maxLength: 100,
    },
    {
      id: "des",
      label: "Description",
      placeholder: {
        EN: "Enter Description",
        CN: "输入描述",
      },
      type: "textarea",
      required: true,
      maxLength: 200,
    },
    {
      id: "day",
      label: "Day",
      placeholder: {
        EN: "Enter Day",
        CN: "输入天数",
      },
      type: "text",
      required: true,
      pattern: /^\d+$/,
    },
    {
      id: "price",
      label: "Price",
      placeholder: {
        EN: "Enter Price",
        CN: "输入价格",
      },
      type: "text",
      required: true,
      pattern: /^\d*\.?\d{0,2}$/,
    },
    {
      id: "startDate",
      label: "Start Date",
      placeholder: {
        EN: "Select start date",
        CN: "选择开始日期",
      },
      type: "date",
      required: true,
    },
    {
      id: "endDate",
      label: "End Date",
      placeholder: {
        EN: "Select end date",
        CN: "选择结束日期",
      },
      type: "date",
      required: true,
    },
    {
      id: "promoOnly",
      label: "Promo Only",
      type: "checkbox",
    },
    {
      id: "showOnHomepage",
      label: "Show on Homepage",
      type: "checkbox",
    },
    {
      id: "displaySequence",
      label: "Display Sequence",
      placeholder: {
        EN: "Enter Display Sequence",
        CN: "输入显示顺序",
      },
      type: "text",
      required: false,
      pattern: /^\d+$/,
    },
    {
      id: "note",
      label: "Note",
      placeholder: {
        EN: "Enter Note",
        CN: "Enter Note",
      },
      type: "text",
      required: false,
    },
  ];

  const productState = useSelector((state) => state.product);
  const product = productState.data || {};
  const status = productState.status;

  const [activeTab, setActiveTab] = useState(TAB_PRODUCT_KEY.basicInfo);
  const [language, setLanguage] = useState(initLanguage);
  const [isLoading, setIsLoading] = useState(false);
  const [isShowDeleteAsset, setIsShowDeleteAsset] = useState(null);
  const [isShowAlert, setIsShowAlert] = useState(null);
  const [isAlertSuccess, setIAlertSuccess] = useState(null);
  const [tabErrors, setTabErrors] = useState({});

  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState(null);
  const selectedKey = useRef(null);
  const selectedTab = useRef(null);

  useEffect(() => {
    if (formType === FORM_TYPE.EDIT) {
      dispatch(fetchProduct({ productId, language }));
    }
    if (formType === FORM_TYPE.CREATE) {
      dispatch(resetProductValue({ language }));
    }
  }, [formType, productId, language, dispatch]);

  const toggleLanguage = (selectedLanguage) => {
    if (formType === FORM_TYPE.CREATE && selectedLanguage === "CN") {
      return;
    }
    setLanguage(selectedLanguage);
    if (formType === FORM_TYPE.EDIT) {
      dispatch(fetchProduct({ productId, language: selectedLanguage }));
    }
    if (formType === FORM_TYPE.CREATE) {
      dispatch(resetProductValue({ language: selectedLanguage }));
    }
  };

  const handleInputChangeBasicInfo = (key, newValue) => {
    dispatch(setValueBasicInfo({ key, value: newValue }));
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleSave = async () => {
    const error = validationData();

    if (isEmptyObj(error)) {
      dispatch(setError(null));
      setTabErrors({});
      setIsLoading(true);
      try {
        const basicInfo = product.basicInfo || product.contentData?.basicInfo || {};

        let displaySequenceValue = basicInfo.displaySequence;
        if (basicInfo.showOnHomepage) {
          if (isEmpty(displaySequenceValue)) {
            displaySequenceValue = null;
          }
        } else {
          displaySequenceValue = undefined;
        }

        const productData = {
          contentData: {
            basicInfo: {
              sku: basicInfo.sku || "",
              day: basicInfo.day || "",
              name: basicInfo.name || "",
              des: basicInfo.des || "",
              price: basicInfo.price || "",
              startDate: basicInfo.startDate || "",
              endDate: basicInfo.endDate || "",
              promoOnly: basicInfo.promoOnly || false,
              showOnHomepage: basicInfo.showOnHomepage || false,
              note: basicInfo.note || ""
            }
          },
          region: product.region || "SG",
          language: language,
          languages: product.languages || ["EN", "CN"]
        };

        if (basicInfo.showOnHomepage) {
          productData.contentData.basicInfo.displaySequence = displaySequenceValue;
        }

        let response;
        if (formType === FORM_TYPE.EDIT) {
          response = await dispatch(
            updateProduct({ uuid: productId, language, payload: productData })
          );
        } else if (formType === FORM_TYPE.CREATE) {
          response = await dispatch(createProducts(productData));
        }

        if (response?.payload?.status === STATUS_CODE_RESPONSE_OK) {
          handleAlertShow(
            formType === FORM_TYPE.EDIT
              ? "Product updated successfully!"
              : "Product created successfully!",
            true
          );
          setTimeout(() => {
            handleComplete();
          }, 1500);
        } else if (response?.error) {
          handleAlertShow(response.error.message, false);
        }
      } catch (err) {
        handleAlertShow(err.message || "An error occurred", false);
      } finally {
        setIsLoading(false);
      }
    } else {
      dispatch(setError(error));
      const newTabErrors = {};
      Object.keys(error).forEach(() => {
        newTabErrors[TAB_PRODUCT_KEY.basicInfo] = true;
      });
      setTabErrors(newTabErrors);
    }
  };

  const handleAlertShow = (message, isSuccess) => {
    setIsShowAlert(message);
    setIAlertSuccess(isSuccess);
  };

  const handleAlertClose = () => {
    setIsShowAlert(null);
    setIAlertSuccess(null);
  };

  const validationData = () => {
    const error = {};
    PRODUCT_MODEL_TEMPLATE.forEach((template) => {
      if (template.required && template.type !== "checkbox") {
        const basicInfo = product.basicInfo || product.contentData?.basicInfo || {};
        const value = basicInfo[template.id];

        if (template.id === "displaySequence") {
          if (basicInfo.showOnHomepage && (isEmpty(value) || value === null)) {
            error[template.id] = MESSAGE_ERROR_INPUT_REQUIRED;
          }
        } else if (template.required) {
          if (isEmpty(value)) {
            error[template.id] = MESSAGE_ERROR_INPUT_REQUIRED;
          }
        }
      }
    });
    return error;
  };

  const handleFileUpload = (file) => {
    if (!file) {
      setUploadErrorMessage("Please upload file.");
      return;
    }
    setIsLoading(true);

    const formData = new FormData();
    formData.append("uuid", formType === FORM_TYPE.EDIT ? productId : "");
    formData.append("assetLocation", ASSET_LOCATION_MAPPING[selectedKey.current]);
    formData.append("assetType", ASSET_TYPE_MAPPING.product);
    formData.append("file", file);

    dispatch(uploadAsset(formData))
      .then((res) => {
        if (res?.payload) {
          const fileId = res.payload.resdata.fileId;
          if (selectedTab.current === TAB_PRODUCT_KEY.basicInfo) {
            handleInputChangeBasicInfo(selectedKey.current, fileId);
          }
        }
        if (res?.error) {
          handleAlertShow(res.error.message, false);
        }
      })
      .catch((err) => {
        handleAlertShow(err.message || "Upload failed", false);
      })
      .finally(() => {
        setIsLoading(false);
        setShowFileUploadModal(false);
        selectedKey.current = null;
        selectedTab.current = null;
      });
  };

  const handleShowUploadFile = (id, tab) => {
    setShowFileUploadModal(true);
    selectedKey.current = id;
    selectedTab.current = tab;
  };

  const handleShowDeleteFile = (id, tab) => {
    setIsShowDeleteAsset(LABEL_DELETE_ASSET);
    selectedKey.current = id;
    selectedTab.current = tab;
  };

  const handleDeleteFile = () => {
    const basicInfo = product.basicInfo || product.contentData?.basicInfo || {};
    const fileId = basicInfo[selectedKey.current];

    if (fileId) {
      dispatch(deleteAsset(fileId))
        .then((res) => {
          if (res?.payload || res?.error?.message.includes("No active document")) {
            handleInputChangeBasicInfo(selectedKey.current, "");
          }
          if (res?.error) {
            handleAlertShow(res.error.message, false);
          }
        })
        .catch((err) => {
          handleAlertShow(err.message || "Delete failed", false);
        })
        .finally(() => {
          setIsShowDeleteAsset(null);
          selectedKey.current = null;
          selectedTab.current = null;
        });
    } else {
      setIsShowDeleteAsset(null);
    }
  };

  const handleCancelUploadFile = () => {
    setShowFileUploadModal(false);
    selectedKey.current = null;
  };

  return (
    <div className="create-user-container">
      <div className="package-wrapper">
        <div className="package-section-title">
          {formType === FORM_TYPE.EDIT ? "Edit Product" : "Create Product"}
        </div>

        <div className="package-language-toggle" style={{ display: "none" }}>
          {LANGUAGE_OPTIONS.map((option) => {
            const isDisabled = formType === FORM_TYPE.CREATE && option.value === "CN";

            return (
              <button
                key={option.id}
                onClick={() => !isDisabled && toggleLanguage(option.value)}
                className={`package-language-option ${language === option.value ? "package-language-active" : ""
                  } ${isDisabled ? "package-language-disabled" : ""}`}
                disabled={isDisabled}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        <div className="package-detail-edit-container">
          <div className="tabs-navigation">
            {TAB_LIST_MAPPING.map((tab, index) => (
              <button
                key={`tab_${index}`}
                className={`tab-button ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => handleTabChange(tab.id)}
              >
                {tab.label}
                {tabErrors[tab.id] && <span style={{ color: 'red' }}>*</span>}
              </button>
            ))}
          </div>

          <div className="tab-content">
            {activeTab === TAB_PRODUCT_KEY.basicInfo &&
              status === API_STATE.SUCCEEDED && (
                <Product
                  templateList={PRODUCT_MODEL_TEMPLATE}
                  language={language}
                  onInputChange={handleInputChangeBasicInfo}
                  onShowUploadFile={(id) =>
                    handleShowUploadFile(id, TAB_PRODUCT_KEY.basicInfo)
                  }
                  onShowDeleteFile={(id) =>
                    handleShowDeleteFile(id, TAB_PRODUCT_KEY.basicInfo)
                  }
                />
              )}
          </div>
        </div>

        <div className="button-group">
          <button
            className="package-detail-edit-back"
            disabled={isLoading}
            onClick={handleBack}
          >
            Back
          </button>

          <button
            className="package-detail-edit-save"
            disabled={isLoading}
            onClick={handleSave}
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <CommonDeleteModal
        show={!isEmpty(isShowDeleteAsset)}
        onHide={() => setIsShowDeleteAsset(null)}
        onConfirm={handleDeleteFile}
      />

      <CommonSuccessModal
        showModal={!isEmpty(isShowAlert)}
        handleClose={handleAlertClose}
        message={isShowAlert}
        status={isAlertSuccess}
      />

      <ModalFileUpload
        show={showFileUploadModal}
        handleCancel={handleCancelUploadFile}
        handleUpload={(file) => handleFileUpload(file)}
        isLoading={isLoading}
        errorMessage={uploadErrorMessage}
      />
    </div>
  );
}

export default ProductDetailEdit;
